import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Box from "@mui/material/Box";
import { styled, Theme, useMediaQuery } from "@mui/material";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import {
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { DateCell } from "_shared/_views/tables/date-cells";
import { StatusChip } from "_shared/_views/tables/status-chip";
import { formatDate } from "_shared/formatting/dates";
import { invitationStatusTone } from "legal-entities/invitations/statuses/invitation-status-tone";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import UserEntityMemberInvitation, {
  MemberInvitationNotOpenError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import {
  type ActiveInvitationStatus,
  useCancelUserEntityMemberInvitationMutation,
  useGetActiveUserEntityMemberInvitationsQuery,
  UserEntityMemberInvitationOrderField,
} from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { ToolbarButtons, ToolbarButtonsContainer } from "_shared/_views/tables/toolbar-buttons";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";

type EntityMemberInvitationsTableProps = {
  canCancelInvitations: boolean;
  /** The page's way to invite people, drawn in the wide layout's toolbar. Absent when it has none. */
  inviteAction?: ReactNode;
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<UserEntityMemberInvitation> }) => ReactNode;
};

const invitationColumnHelper = createMRTColumnHelper<UserEntityMemberInvitation>();

/** The Name column's id, and the column the server sorts by name on. */
export const NAME_COLUMN = "name";
/** The Email column's id. It folds under the name when the pane is short of room (#6722). */
export const EMAIL_COLUMN = "email";
/** The Status column's id, and the column whose filter the server applies. */
export const STATUS_COLUMN = "status";
/** The Sent column's id, and the column the server sorts by date sent on. */
export const SENT_COLUMN = "sent";
/** The Expires on column's id. */
export const EXPIRES_COLUMN = "expires";

/** The statuses an active invitation can have, and so the only ones worth offering as a filter. */
const ACTIVE_STATUSES: ActiveInvitationStatus[] = [InvitationStatus.Open, InvitationStatus.Expired];

/**
 * Newest first, which is the server's own default. Never empty: the server orders every answer, so
 * "unsorted" would be an arrow meaning nothing.
 */
const DEFAULT_SORTING: SortingState = [{ id: SENT_COLUMN, desc: true }];


/**
 * The server's order for the table's sort state (#5444). Only Name and Sent are sortable, because the
 * server orders by nothing else.
 */
export function invitationOrderFrom(sorting: SortingState): {
  orderBy: UserEntityMemberInvitationOrderField;
  orderDir: OrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : undefined;
  return {
    orderBy:
      first?.id === NAME_COLUMN ? UserEntityMemberInvitationOrderField.Name : UserEntityMemberInvitationOrderField.DateSent,
    orderDir: first?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  };
}

/** The status picked in the Status column's filter, if it is one the server filters on. */
export function invitationStatusFrom(columnFilters: ColumnFiltersState): ActiveInvitationStatus | undefined {
  const value = columnFilters.find((filter) => filter.id === STATUS_COLUMN)?.value;
  return ACTIVE_STATUSES.find((status) => status === value);
}

/** The invitee's name as the row shows it, and as the server searches it: first, then last. */
function inviteeName(invitation: UserEntityMemberInvitation): string {
  return `${invitation.registrationInfo.name.firstName} ${invitation.registrationInfo.name.lastName}`;
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

export default function EntityMemberInvitationsTable(props: Readonly<EntityMemberInvitationsTableProps>) {
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { canCancelInvitations, inviteAction, renderTopToolbarCustomActions } = props;
  const session = useSession();
  const confirm = useConfirmDialog();
  const [cancelUserEntityMemberInvitation] = useCancelUserEntityMemberInvitationMutation();
  const [cancelingInvitationId, setCancelingInvitationId] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: LIST_PAGE_SIZE });

  // The search, the status, the order and the page all go to the server (#5444). A bulk invitation
  // of a few hundred people used to arrive whole and be paged in the browser.
  const order = invitationOrderFrom(sorting);
  const {
    currentData: page,
    data: lastAnswer,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useGetActiveUserEntityMemberInvitationsQuery({
    entityId: session.user!.legalEntityId,
    search: globalFilter,
    status: invitationStatusFrom(columnFilters),
    orderBy: order.orderBy,
    orderDir: order.orderDir,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });
  // currentData, not data: data keeps the last answer while another loads and after it fails, which
  // would show the rows of a search already replaced, under its error.
  const invitations = page?.data;

  // Cancelling the only invitation on the last page leaves that page empty. Step back to the new
  // last page rather than show an empty table with invitations behind it. Adjusted while rendering
  // rather than in an effect, so the empty page is never drawn.
  const totalPages = page?.headers.totalPages ?? 0;
  if (totalPages > 0 && pagination.pageIndex >= totalPages) {
    setPagination((current) => ({ ...current, pageIndex: totalPages - 1 }));
  }

  const cancelInvitation = useCallback(
    async (invitation: UserEntityMemberInvitation) => {
      if (!invitation.id || invitation.status !== InvitationStatus.Open) return;

      const response = await confirm({
        title: "Cancel invitation?",
        message: `Cancel the invitation for ${invitation.registrationInfo.email.value}?`,
        okButtonText: "Cancel invitation",
        cancelButtonText: "Keep invitation",
      });
      if (response === ConfirmResponse.Cancel) return;

      try {
        setCancelingInvitationId(invitation.id.value);
        await cancelUserEntityMemberInvitation({ invitation }).unwrap();
        enqueueSnackbar("Invitation canceled", { variant: "success" });
      } catch (cancelError) {
        console.error(cancelError);
        enqueueSnackbar(
          cancelError instanceof MemberInvitationNotOpenError
            ? cancelError.message
            : "Failed to cancel invitation",
          { variant: "error" },
        );
      } finally {
        setCancelingInvitationId(null);
      }
    },
    [cancelUserEntityMemberInvitation, confirm],
  );

  const renderNameColumn = useCallback((
    invitation: UserEntityMemberInvitation,
    table: MRT_TableInstance<UserEntityMemberInvitation>,
  ) => (
    <NameCellContainer>
      <IndividualAvatar />
      {/* An invitation opens nothing, so the name is plain text rather than a link. */}
      <CondensedCell title={<SearchMatch table={table} text={inviteeName(invitation)} />} />
    </NameCellContainer>
  ), []);

  const columns = useMemo(
    () => [
      // The search matches the name and the email address; the name cell marks its own text (#6590).
      invitationColumnHelper.accessor(inviteeName, {
        header: "Name",
        id: NAME_COLUMN,
        Cell: ({ row, table }) => renderNameColumn(row.original, table),
        enableColumnFilter: false,
        meta: { kind: "name" },
      }),
      // Searched with the name, but not sorted on its own: the server orders by name, then email. Its
      // own column where there is room, and under the name where there is not (#6722).
      invitationColumnHelper.accessor((invitation) => invitation.registrationInfo.email.value, {
        header: "Email",
        id: EMAIL_COLUMN,
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          kind: "text",
          fold: { into: NAME_COLUMN, priority: 1, render: (invitation) => invitation.registrationInfo.email.value },
        },
      }),
      invitationColumnHelper.accessor((invitation) => invitation.status, {
        header: "Status",
        id: STATUS_COLUMN,
        // Not searched, so never marked as matching.
        enableGlobalFilter: false,
        enableSorting: false,
        filterVariant: "select",
        filterSelectOptions: ACTIVE_STATUSES,
        meta: { kind: "status" },
        Cell: ({ row }) => (
          <StatusChip label={row.original.status} tone={invitationStatusTone(row.original.status)} />
        ),
      }),
      invitationColumnHelper.accessor((invitation) => formatDate(invitation.sentDate) ?? "", {
        header: "Sent",
        id: SENT_COLUMN,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        meta: { kind: "date" },
        Cell: ({ row }) => <DateCell value={row.original.sentDate} />,
      }),
      invitationColumnHelper.accessor((invitation) => formatDate(invitation.expires) ?? "", {
        header: "Expires",
        id: EXPIRES_COLUMN,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: { kind: "date" },
        Cell: ({ row }) => <DateCell value={row.original.expires} />,
      }),
    ],
    [renderNameColumn],
  );

  const renderToolbarInternalActions = useCallback(
    (toolbarProps: { table: MRT_TableInstance<UserEntityMemberInvitation> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={toolbarProps.table} sx={searchFieldSpacing} />
              <TableToolsMenu table={toolbarProps.table} />
            </ToolbarButtons>
            {inviteAction}
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    [inviteAction, isMediumDisplaySize],
  );
  const options: MRT_TableOptions<UserEntityMemberInvitation> = {
    columns,
    data: invitations ?? [],
    enableColumnFilters: true,
    enableRowActions: canCancelInvitations,
    // Pinned to the right edge, as on the communications page. Last in the column order it sat past
    // Status and Expires on a phone, where Name and Email already fill the screen, so the only way to
    // cancel an invitation was a sideways scroll nobody knew to make.
    enableColumnPinning: true,
    initialState: { columnPinning: { right: ["mrt-row-actions"] }, showGlobalFilter: true },
    // No column-level sizing or cell props for the actions column: the shared table shrinks it to its
    // one cancel button. Cell props set here would replace that rule rather than add to it, and MRT's
    // `size` does nothing in this semantic layout.
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "pending invitations", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      isLoading: isFetching,
      onRetry: () => void refetch(),
      subject: "pending invitations",
    }),
    enableRowSelection: false,
    positionActionsColumn: "last",
    enableStickyFooter: true,
    enableStickyHeader: true,
    getRowId: (invitation, index) => invitation.id?.value ?? `skeleton-${index}`,
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTableContainerProps: {
      sx: { flex: 1 },
    },
    muiTablePaperProps: {
      sx: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: error
      ? {
          children: "Error loading invitations",
          color: "error",
        }
      : undefined,
    renderRowActions: canCancelInvitations ? ({ row }) => {
      const invitation = row.original;
      const isCanceling = cancelingInvitationId === invitation.id?.value;
      const isCancelable = invitation.status === InvitationStatus.Open && Boolean(invitation.id);
      return (
        <RowActions
          ariaLabel={`Actions for the invitation to ${invitation.registrationInfo.email.value}`}
          actions={[
            {
              key: "cancel",
              label: isCanceling ? "Canceling..." : "Cancel invitation",
              ariaLabel: `Cancel the invitation to ${invitation.registrationInfo.email.value}`,
              icon: <CancelOutlinedIcon />,
              destructive: true,
              onClick: () => void cancelInvitation(invitation),
              disabled: !isCancelable || isCanceling,
              disabledReason: isCanceling ? "Canceling..." : "Only an open invitation can be canceled",
            },
          ]}
        />
      );
    } : undefined,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: false,
    // The server orders by one column, so a second sort indicator would promise an order it never sends.
    enableMultiSort: false,
    // A new search, filter or order is a new list, and a new list starts on its first page.
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    // Every invitation that matches, from X-Pagination, not the length of the page in hand. While a
    // page the cache has never held loads, currentData is undefined but isLoading is false, so a
    // count of zero would let MRT judge the requested page out of bounds and send the reader back
    // to page one before it arrived. The last answer's total holds the page until the new one lands.
    rowCount: (page ?? lastAnswer)?.headers.totalElements ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: Boolean(error),
      showProgressBars: isFetching,
      sorting,
    },
  };

  return <MRTNoMemo<UserEntityMemberInvitation> options={options} cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
